import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { LoaderState } from '../enums/loader';
import { CouponState } from '../enums/coupon';
import User from '../models/user';
import { Tier, TierPricesData } from '../models/tiers';
import i18n from '../utils/translations/i18n';
import { Address } from '../enums/checkoutShipping';
import { Page } from '../enums/page';
import { logSentryTransacation } from '../utils/sentry';
import { ProductItem } from '../models/reorderProductItem';

const initialState = {
    user: new User(),
    currentPage: null,
    ctRecsVisible: false,
    waitForPaymentsCall: true,
    waitForRecalculate: true,
    showReorderSlider: true,
    doesDiscountMatchesCart:false,
    isReorderImpressionFired:false,
    globalThresholdMet: false,
    isShippingDisc:false,
    validatedCoupon:{
        state: CouponState.INITIAL,
    },
    doesThresholdExist:true,
    doesCouponThresholdExist:false,
    isCouponAppliedOnShipping:false,
    globalDiscounDescription:'',
    globalDiscountShippingType:'',
    vatToken:null,
    loadingState: LoaderState.STOP,
    shippingPrefill : false,
    customerStatus: '',
    coupon: {
        state: CouponState.INITIAL,
        code: '',
        description: '',
        error: '',
        details:'',
        thresholdValue:null,
    },
    shippingMethods: [],
    error: {
        isError: false,
        errorObj: {
            message: "",
            operation: "",
            mainItemId: ""
        }
    },
    emailPreference : {
        emailAddress: '',
        subscribe: ''
    },
    paymentMethod: '',
    checkoutType: 'national pen checkout',
    currentOperation: '',
    ongoingAction: '',
    disabledItem: false,
    viewSimilarFailure:false,
    showWarning: false,
    currentMyAccountSection: 'dashboard',
    orderDetailsId: '',
    myAccountAddressForm : {},
    addAddressFormState: { showForm: false, formType: Address.SHIPPING, error: '' },
    buyerTypeUpdated: false, //rtb or ftb
    cartOperation: {
        mainItemId: ""
    },
    redirectCartToHomePage: false,
    isGlobalCartDiscountFree: false,
    isShippingCouponFree: false,
    doNotShowPayLater: false,
    evaluateOrder: { dataReOrder: [] },
    pdpLinkForOrders: {},
    clientRefresh: false,
    reorderFailMsg: {
        errorMsg : '',
        lineItemId: ''
    },
    sentryNavigationTrackingState: {
        [Page.SHOPPINGBAG]: {finish: ()=> console.log(`Logging ${Page.SHOPPINGBAG}`)},
        [Page.CHECKOUTSHIPPING]: {finish: ()=> console.log(`Logging ${Page.CHECKOUTSHIPPING}`)},
        [Page.CHECKOUTPAYMENT]: {finish: ()=> console.log(`Logging ${Page.CHECKOUTSHIPPING}`)},
    },
    sentryTransactionState : {
        applyCoupon : {finish: ()=> console.log(`Apply Coupon Transaction`)},
    },
    reorderProducts: [] as ProductItem[],
    tierPricesData: {} as TierPricesData,
    checkOOSStatus: false,
    lastQtyEditMethod: '',
};

const reducers = {
    evaluateOrder(state, action) {
        state.evaluateOrder = action.payload.evaluateOrder;
    },
    pdpLinkForOrders(state, action) {
        state.pdpLinkForOrders = action.payload.pdpLinkForOrders;
    },
    reorderFailMsg(state, action) {
        state.reorderFailMsg = action.payload.reorderFailMsg;
    },
    user(state, action) {
        state.user = action.payload.user
    },
    loading(state, action) {
        state.loadingState = action.payload.loadingState;
    },
    shippingPrefill(state,action){
        state.shippingPrefill = action.payload.shippingPrefill
    },
    currentPage(state, action) {
        state.currentPage = action.payload.currentPage
    },
    ctRecsVisible(state, action) {
        state.ctRecsVisible = action.payload.ctRecsVisible
    },
    waitForPaymentsCall(state, action) {
        state.waitForPaymentsCall = action.payload.waitForPaymentsCall
    },
    waitForRecalculate(state, action) {
        state.waitForRecalculate = action.payload.waitForRecalculate
    },
    showReorderSlider(state, action) {
        state.showReorderSlider = action.payload.showReorderSlider
    },
    globalThresholdMet(state, action) {
        state.globalThresholdMet = action.payload.globalThresholdMet
    },
    isShippingDisc(state, action) {
        state.isShippingDisc = action.payload.isShippingDisc
    },
    validatedCoupon(state, action) {
        state.validatedCoupon = action.payload.validatedCoupon
    },
    doesDiscountMatchesCart(state, action) {
        state.doesDiscountMatchesCart = action.payload.doesDiscountMatchesCart
    },
    isReorderImpressionFired(state, action) {
        state.isReorderImpressionFired = action.payload.isReorderImpressionFired
    },
    doesThresholdExist(state, action) {
        state.doesThresholdExist = action.payload.doesThresholdExist
    },
    doesCouponThresholdExist(state, action) {
        state.doesCouponThresholdExist = action.payload.doesCouponThresholdExist
    },
    isCouponAppliedOnShipping(state, action) {
        state.isCouponAppliedOnShipping = action.payload.isCouponAppliedOnShipping
    },
    globalDiscounDescription(state, action) {
        state.globalDiscounDescription = action.payload.globalDiscounDescription
    },
    globalDiscountShippingType(state, action) {
        state.globalDiscountShippingType = action.payload.globalDiscountShippingType
    },
    vatToken(state, action) {
        state.vatToken = action.payload.vatToken
    },
    coupon(state,action){
        state.coupon = action.payload.coupon
    },
    shippingMethods(state, action) {
        state.shippingMethods = action.payload.shippingMethods
    },
    reorderProducts(state, action) {
        state.reorderProducts = action.payload.reorderProducts
    },
    currentOperation(state, action) {
        state.currentOperation = action.payload.currentOperation
    },
    ongoingAction(state, action) {
        state.ongoingAction = action.payload.ongoingAction
    },
    paymentMethod(state, action) {
        state.paymentMethod = action.payload.paymentMethod
    },
    checkoutType(state, action) {
        state.checkoutType = action.payload.checkoutType
    },
    error(state,action){
        state.error = action.payload.error
    },
    disabledItem(state, action){
        state.disabledItem = action.payload.disabledItem
    },
    viewSimilarFailure(state, action){
        state.viewSimilarFailure = action.payload.viewSimilarFailure
    },
    myAccountSection(state, action){
        state.currentMyAccountSection = action.payload.section
    },
    emailPreference(state, action){
        state.emailPreference = action.payload.emailPreference
    },
    orderDetailsId(state, action){
      state.orderDetailsId = action.payload.orderDetailsId
    },
    myAccountAddressForm(state, action) {
        state.myAccountAddressForm = action.payload.myAccountAddressForm;
    },
    addAddressFormState(state, action) {
        state.addAddressFormState = action.payload.addAddressFormState;
    },
    buyerTypeUpdated(state, action){
        state.buyerTypeUpdated = action.payload.buyerTypeUpdated
    },
    cartOperation(state, action) {
        state.cartOperation = action.payload.cartOperation;
    },
    redirectCartToHomePage(state, action) {
      state.redirectCartToHomePage = action.payload.redirectCartToHomePage;
    },
    isGlobalCartDiscountFree(state, action) {
      state.isGlobalCartDiscountFree = action.payload.isGlobalCartDiscountFree;
    },
    isShippingCouponFree(state, action) {
      state.isShippingCouponFree = action.payload.isShippingCouponFree;
    },
    doNotShowPayLater(state, action) {
        state.doNotShowPayLater = action.payload.doNotShowPayLater;
    },
    clientRefresh(state, action) {
        state.clientRefresh = action.payload.clientRefresh;
    },
    showWarning(state, action){
        state.showWarning = action.payload.showWarning;
    },
    sentryNavigationTrackingState(state,action){
        const {fromPage , toPage} = action.payload;
        state.sentryNavigationTrackingState[toPage] = logSentryTransacation(`${fromPage} to ${toPage} Page Transition`);
    },
    sentryTransactionState(state,action){
        const {transactionName , transactionMessage} = action.payload;
        state.sentryTransactionState[transactionName] = logSentryTransacation(`${transactionMessage}`); 
    },
    tierPricesData: (state, action: PayloadAction<{ productKey: string; tierPricesData: Tier }>) => {
        const { productKey, tierPricesData } = action.payload;
        state.tierPricesData[productKey] = tierPricesData;
    },
    checkOOSStatus(state, action) {
        state.checkOOSStatus = action.payload.checkOOSStatus;
    },
    lastQtyEditMethod(state, action) {
        state.lastQtyEditMethod = action.payload.lastQtyEditMethod;
    },
    customerStatus(state, action) {
        state.customerStatus = action.payload.customerStatus;
    },
}

const applicationSlice = createSlice({
    name: 'applicationState',
    initialState: initialState,
    reducers: reducers
});

const {
    user, currentPage, ctRecsVisible, waitForPaymentsCall, waitForRecalculate, doesDiscountMatchesCart,
    isShippingDisc, doesCouponThresholdExist, isCouponAppliedOnShipping, validatedCoupon, globalThresholdMet,
    doesThresholdExist, globalDiscountShippingType, globalDiscounDescription, vatToken, loading, shippingPrefill,
    coupon, evaluateOrder, pdpLinkForOrders, shippingMethods, paymentMethod, error, currentOperation, ongoingAction,
    checkoutType, disabledItem,viewSimilarFailure, buyerTypeUpdated, cartOperation, isGlobalCartDiscountFree, isShippingCouponFree,
    redirectCartToHomePage, doNotShowPayLater, myAccountSection, emailPreference, myAccountAddressForm,
    addAddressFormState, orderDetailsId, clientRefresh, reorderFailMsg, showWarning, sentryNavigationTrackingState,
    sentryTransactionState, reorderProducts, showReorderSlider, isReorderImpressionFired, tierPricesData, checkOOSStatus,
    lastQtyEditMethod, customerStatus
} = applicationSlice.actions;


export const setUserState = (userObj: any) => (dispatch) => dispatch(user({ user: new User(userObj) }));
export const setCurrentPage = (currentPageStr: string) => (dispatch) => dispatch(currentPage({currentPage: currentPageStr}));
export const setCtRecsVisible = (ctRecsVisibleBool: boolean) => (dispatch) => dispatch(ctRecsVisible({ctRecsVisible: ctRecsVisibleBool}));
export const setIsGlobalCartDiscountFree = (isDiscountFree: boolean) => (dispatch) => dispatch(isGlobalCartDiscountFree({isGlobalCartDiscountFree: isDiscountFree}));
export const setIsShippingCouponFree= (isCouponFree: boolean) => (dispatch) => dispatch(isShippingCouponFree({isShippingCouponFree: isCouponFree}));
export const setWaitForPaymentsCall = (paymentsFlag: boolean) => (dispatch) => dispatch(waitForPaymentsCall({waitForPaymentsCall: paymentsFlag}));
export const setWaitForRecalculate = (validFlag: boolean) => (dispatch) => dispatch(waitForRecalculate({waitForRecalculate: validFlag}));
export const setShowReorderSlider = (validFlag: boolean) => (dispatch) => dispatch(showReorderSlider({showReorderSlider: validFlag}));
export const setDoesDiscountMatchesCart = (matchesCart: boolean) => (dispatch) => dispatch(doesDiscountMatchesCart({doesDiscountMatchesCart: matchesCart}));
export const setIsReorderImpressionFired = (matchesCart: boolean) => (dispatch) => dispatch(isReorderImpressionFired({isReorderImpressionFired: matchesCart}));
export const setGlobalThresholdMet = (thresholdMet: boolean) => (dispatch) => dispatch(globalThresholdMet({globalThresholdMet: thresholdMet}));
export const setIsShippingDisc = (isShipping: boolean) => (dispatch) => dispatch(isShippingDisc({isShippingDisc: isShipping}));
export const setValidatedCoupon = (isValid) => (dispatch) => dispatch(validatedCoupon({validatedCoupon: isValid}));
export const setDoesThresholdExist = (thresholdFlag: boolean) => (dispatch) => dispatch(doesThresholdExist({doesThresholdExist: thresholdFlag}));
export const setDoesCouponThresholdExist = (couponThresholdFlag: boolean) => (dispatch) => dispatch(doesCouponThresholdExist({doesCouponThresholdExist: couponThresholdFlag}));
export const setIsCouponAppliedOnShipping = (couponApplied: boolean) => (dispatch) => dispatch(isCouponAppliedOnShipping({isCouponAppliedOnShipping: couponApplied}));
export const setGlobalDiscounDescription = (discountDesc: string) => (dispatch) => dispatch(globalDiscounDescription({globalDiscounDescription: discountDesc}));
export const setGlobalDiscountShippingType = (shippingType: string) => (dispatch) => dispatch(globalDiscountShippingType({globalDiscountShippingType: shippingType}));
export const setVatToken = (vatTokenStr: string) => (dispatch) => dispatch(vatToken({vatToken: vatTokenStr}));
export const startLoading = () => (dispatch) => dispatch(loading({ loadingState: LoaderState.START }));
export const stopLoading = () => (dispatch) => dispatch(loading({ loadingState: LoaderState.STOP }));
export const setShippingPrefill = (shippingPrefillFlag) => (dispatch) => dispatch(shippingPrefill({shippingPrefill : shippingPrefillFlag}));
export const setCoupon = (couponObj) => (dispatch) => dispatch(coupon({ coupon: couponObj}));
export const setShippingMethods = (shippingMethodsObj) => (dispatch) => dispatch(shippingMethods({shippingMethods: shippingMethodsObj}))
export const setCurrentOperation = (currentOperationStr: any) => (dispatch) => dispatch(currentOperation({currentOperation: currentOperationStr}));
export const setOngoingAction = (ongoingActionStr: any) => (dispatch) => dispatch(ongoingAction({ongoingAction: ongoingActionStr}));
export const setPaymentMethod = (paymentMethodStr: string) => (dispatch) => dispatch(paymentMethod({paymentMethod: paymentMethodStr}));
export const setCheckoutType = (checkoutTypeStr: string) => (dispatch) => dispatch(checkoutType({checkoutType: checkoutTypeStr}));      
export const logError = (isError = false, operation, mainItemId) => (dispatch) => dispatch(error({ error:{ isError: isError, errorObj: { message: isError ? i18n.t('error.somethingwentwrong') : null, operation: isError ? operation : null, mainItemId: mainItemId ?? null}}}));        
export const setDisabledItem = (isDisabledItemPresent: boolean = false) => (dispatch) => dispatch(disabledItem({disabledItem: isDisabledItemPresent}));      
export const setViewSimilarFailure = (viewSimilarFlag: boolean = false) => (dispatch) => dispatch(viewSimilarFailure({viewSimilarFailure: viewSimilarFlag}));      
export const setMyAccountSection = (section: string) => (dispatch) => dispatch(myAccountSection({section: section})); 
export const setEmailPreference = (emailPrefObj) => (dispatch) => dispatch(emailPreference({emailPreference: emailPrefObj}));
export const setOrderDetailsId = (orderId) => (dispatch) => dispatch(orderDetailsId({orderDetailsId: orderId}));
export const setMyAccountAddressForm = (myAccountAddressObj) => (dispatch) => dispatch(myAccountAddressForm({myAccountAddressForm: myAccountAddressObj}));
export const setAddAddressFormState = (addAddressFormStateObj) => (dispatch) => dispatch(addAddressFormState({addAddressFormState: addAddressFormStateObj}));
export const setBuyerTypeUpdated = (isBuyerTypeUpdated: boolean = false) => (dispatch) => dispatch(buyerTypeUpdated({buyerTypeUpdated: isBuyerTypeUpdated}));      

export const setCartOperation = (cartOperationObj) => (dispatch) => dispatch(cartOperation({cartOperation: cartOperationObj}));
export const setRedirectCartToHomePage = (doRedirectCartToHomePage: boolean = false) => (dispatch) => dispatch(redirectCartToHomePage({redirectCartToHomePage: doRedirectCartToHomePage}));
export const setdoNotShowPayLater = (setdoNotShowPayLater: boolean = false) => (dispatch) => dispatch(doNotShowPayLater({doNotShowPayLater: setdoNotShowPayLater}));
export const setevaluateOrder = (setevaluateOrder: any) => (dispatch) => dispatch(evaluateOrder({evaluateOrder: setevaluateOrder}));
export const setpdpLinkForOrders = (setpdpLinkForOrders: any) => (dispatch) => dispatch(pdpLinkForOrders({pdpLinkForOrders: setpdpLinkForOrders}));
export const setreorderFailMsg = (setreorderFailMsg: any) => (dispatch) => dispatch(reorderFailMsg({reorderFailMsg: setreorderFailMsg}));

export const setClientRefresh = (isClientRefresh: boolean = false) => (dispatch) => dispatch(clientRefresh({clientRefresh: isClientRefresh}));
export const setCheckOOSStatus = (isCheckOOSStatus: boolean = false) => (dispatch) => dispatch(checkOOSStatus({checkOOSStatus: isCheckOOSStatus}));
export const setShowWarningOnCartPage = (setShowWarningOnCartPage: boolean) => (dispatch) => dispatch(showWarning({showWarning: setShowWarningOnCartPage}));
export const setSentryNavigationTrackingState = (fromPage: string, toPage: string) => (dispatch) => dispatch(sentryNavigationTrackingState({fromPage, toPage}));
export const setSentryTransactionState = (transactionName: string, transactionMessage: string) => (dispatch) => dispatch(sentryTransactionState({transactionName, transactionMessage}));
export const setReorderProducts = (reorderProductsObj) => (dispatch) => dispatch(reorderProducts({reorderProducts: reorderProductsObj}));
export const setTierPricesData = (productKey: string, tierPricesDataObj) => (dispatch) =>
    dispatch(tierPricesData({ productKey, tierPricesData: tierPricesDataObj }));  

export const setLastQtyEditMethod = (method: string) => (dispatch) => {
    dispatch(lastQtyEditMethod({ lastQtyEditMethod: method }));
};

export const setCustomerStatus = (customerState: string) => (dispatch) => {
    dispatch(customerStatus({ customerStatus: customerState }));
};

export default applicationSlice;
